import React, { useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  Users,
  Clock,
  Settings,
  BarChart3,
  Calendar,
  Copy,
  Zap,
  Sparkles,
  ArrowRight,
} from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import Footer from "../components/Footer";

const CASE_SUMMARY = [
  {
    icon: Building2,
    title: "Industry",
    desc: (
      <>
        <span>Professional Services</span>
        <span className="block text-[#64748B] mt-0.5">(Consulting & Advisory)</span>
      </>
    ),
  },
  {
    icon: Users,
    title: "Key Users",
    desc: "Partners, Bid Managers, Practice Heads, Consultants",
  },
  {
    icon: Clock,
    title: "Business Need",
    desc: "Faster, more consistent and higher-quality RFP responses.",
  },
  {
    icon: Settings,
    title: "Solution",
    desc: "Simpel Proposal & RFP Intelligence",
  },
  {
    icon: BarChart3,
    title: "Impact",
    desc: (
      <>
        <span>45-60% faster draft creation</span>
        <span className="block mt-0.5">35-50% more partner capacity</span>
      </>
    ),
  },
  {
    icon: Calendar,
    title: "Timeframe",
    desc: "9-15 months payback",
  },
];

const ProfessionalServicesCaseStudy = () => {
  const navigate = useNavigate();
  const containerRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBack = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate("/labs#insights");
    }
  };

  // Scroll tracking across the sticky track matching previous case studies
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  /*
    Animation Timeline:
    0.0 -> 0.22: Overlay enters from bottom (100% -> 0%), opacity (0 -> 1)
    0.22 -> 0.75: Overlay stays PINNED over Hero section (0% -> 0%, opacity 1)
    0.75 -> 0.98: Overlay slides UP out of view (0% -> -100%), opacity (1 -> 0)
    1.0+: Exits smoothly
  */
  const overlayY = useTransform(
    scrollYProgress,
    [0, 0.22, 0.75, 0.98],
    ["100%", "0%", "0%", "-100%"]
  );

  const overlayOpacity = useTransform(
    scrollYProgress,
    [0, 0.06, 0.22, 0.75, 0.95, 1],
    [0, 0.6, 1, 1, 0.7, 0]
  );

  const handleScrollDown = () => {
    const el = document.getElementById("case-at-a-glance");
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    } else if (containerRef.current) {
      const top = containerRef.current.offsetTop;
      const height = containerRef.current.offsetHeight;
      window.scrollTo({
        top: top + height,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#000000] flex flex-col text-white">
      {/* Top Navigation Bar matching exact Screenshot */}
      <header className="sticky top-0 z-50 w-full bg-[#000000] border-b border-white/[0.06] shadow-sm">
        <div className="mx-auto flex h-[76px] sm:h-[82px] max-w-[1440px] items-center justify-between px-6 lg:px-[70px]">
          {/* Simpel Brand Logo */}
          <Link
            to="/"
            className="flex items-center gap-3 sm:gap-3.5 group select-none shrink-0"
          >
            <img
              src="/images/simpel-icon-mark.png"
              alt="Simpel"
              className="h-[34px] sm:h-[38px] w-auto object-contain transition-transform duration-300 group-hover:scale-105"
            />
            <div className="flex flex-col justify-center">
              <span className="text-[22px] sm:text-[25px] font-bold leading-none tracking-tight text-white">
                Simpel
              </span>
              <span className="mt-[3px] text-[7.5px] sm:text-[8px] font-bold uppercase tracking-[2.2px] sm:tracking-[2.5px] text-white/70">
                AUTOMATION SIMPLIFIED
              </span>
            </div>
          </Link>

          {/* Right Breadcrumb & Back Button matching exact Screenshot */}
          <div className="flex items-center gap-5 sm:gap-8">
            <div className="hidden sm:block border-b-2 border-[#2F6DF6] pb-0.5">
              <span className="text-[14px] sm:text-[15px] font-medium text-[#2F6DF6] tracking-wide select-none">
                Case Study / Professional Services
              </span>
            </div>

            <button
              onClick={handleBack}
              className="inline-flex items-center gap-2 rounded-full bg-[#00D2EE] px-5 py-2 sm:px-6 sm:py-2.5 text-[13px] sm:text-[14px] font-bold text-black transition-all duration-200 hover:brightness-105 hover:shadow-[0_4px_16px_rgba(0,210,238,0.35)] active:scale-95 cursor-pointer"
            >
              <ArrowLeft className="h-4 w-4 stroke-[2.5]" />
              <span>Back to Insights</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 w-full bg-[#000000]">
        {/* Scroll Pinning Container (height allows smooth entry, pin duration, and exit) */}
        <section ref={containerRef} className="relative w-full h-[270vh]">
          {/* Sticky Viewport Stage */}
          <div className="sticky top-[76px] sm:top-[82px] h-[calc(100vh-76px)] sm:h-[calc(100vh-82px)] w-full overflow-hidden bg-[#000000]">
            {/* 1. Underlying Hero Section Image */}
            <div className="absolute inset-0 w-full h-full">
              <img
                src="/images/insights/professional-hero.jpg"
                alt="Professional Services - Proposal and RFP Intelligence Meeting"
                className="w-full h-full object-cover object-center block select-none"
              />
            </div>

            {/* 2. Frosted Blur Overlay Section matching user screenshot */}
            <motion.div
              style={{
                y: overlayY,
                opacity: overlayOpacity,
              }}
              className="absolute inset-0 z-20 flex flex-col items-center justify-center px-6 sm:px-12 lg:px-20 text-center"
            >
              {/* Frosted Glass Background Tint & Backdrop Blur */}
              <div
                className="absolute inset-0 w-full h-full pointer-events-none"
                style={{
                  backgroundColor: "rgba(38, 64, 94, 0.82)",
                  backdropFilter: "blur(24px)",
                  WebkitBackdropFilter: "blur(24px)",
                }}
              />

              {/* Foreground Content matching Screenshot */}
              <div className="relative z-10 max-w-[1360px] w-full px-4 flex flex-col items-center pt-4 sm:pt-0">
                {/* Eyebrow */}
                <p className="text-[12px] sm:text-[13px] font-bold tracking-[1.5px] sm:tracking-[2px] text-[#38BDF8] select-none uppercase">
                  Professional Services
                </p>

                {/* Main Heading matching user screenshot (2 Lines) */}
                <h1 className="mt-5 sm:mt-6 text-[26px] sm:text-[34px] md:text-[42px] lg:text-[48px] xl:text-[52px] font-extrabold leading-[1.2] sm:leading-[1.18] text-white tracking-tight">
                  <span className="block whitespace-normal sm:whitespace-nowrap">
                    Stronger Proposals, Faster: From RFP to
                  </span>
                  <span className="block whitespace-normal sm:whitespace-nowrap mt-1">
                    Tailored Response
                  </span>
                </h1>

                {/* Subtitle / Description (2 Lines as in screenshot) */}
                <div className="mt-5 sm:mt-6 max-w-[860px] text-[13.5px] sm:text-[14.5px] md:text-[15.5px] lg:text-[16px] leading-[24px] sm:leading-[26px] text-white/80">
                  <p className="whitespace-normal sm:whitespace-nowrap">
                    How Simpel Proposal & RFP Intelligence helps teams analyse requirements, retrieve relevant expertise and
                  </p>
                  <p className="whitespace-normal sm:whitespace-nowrap mt-0.5">
                    create tailored responses with greater speed and consistency.
                  </p>
                </div>

                {/* Vertical Dashed Line with Downward Arrow */}
                <div
                  onClick={handleScrollDown}
                  className="mt-10 sm:mt-14 lg:mt-16 flex flex-col items-center cursor-pointer group select-none"
                  title="Scroll to continue"
                >
                  <svg
                    width="20"
                    height="126"
                    viewBox="0 0 20 126"
                    fill="none"
                    className="text-white/75 transition-transform duration-300 group-hover:translate-y-1.5"
                  >
                    <line
                      x1="10"
                      y1="0"
                      x2="10"
                      y2="114"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeDasharray="4 4"
                    />
                    <path
                      d="M5 110L10 118L15 110"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* =========================================================================
            Case at a glance Section (matching Screenshot)
            Background: rgba(245, 250, 254, 1)
            6 Cards in a 3-Column x 2-Row Grid
           ========================================================================= */}
        <section
          id="case-at-a-glance"
          className="w-full py-16 sm:py-20 lg:py-24 text-slate-900"
          style={{ backgroundColor: "rgba(245, 250, 254, 1)" }}
        >
          <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            {/* Heading row with gradient text and horizontal divider */}
            <div className="flex items-center justify-between mb-8 sm:mb-10">
              <h2 className="text-[30px] sm:text-[36px] lg:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                Case at a glance
              </h2>
              <div className="hidden sm:block flex-1 h-[1px] bg-[#E2E8F0] ml-8" />
            </div>

            {/* Case Summary 6-Card Grid Container (3 cols x 2 rows) */}
            <div className="w-full rounded-[10px] sm:rounded-[12px] border border-[#EAF2FA] bg-white overflow-hidden shadow-[0_2px_12px_rgba(15,23,42,0.02)]">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                {CASE_SUMMARY.map((item, idx) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={item.title}
                      className={`flex items-start gap-4 p-6 sm:p-7 min-h-[160px] sm:min-h-[175px] transition-colors duration-200 hover:bg-[#F8FAFC]/50 ${
                        idx % 3 !== 2 ? "lg:border-r border-[#EAF2FA]" : ""
                      } ${
                        idx < 3 ? "lg:border-b border-[#EAF2FA]" : ""
                      } ${
                        idx % 2 === 0 ? "md:max-lg:border-r border-[#EAF2FA]" : ""
                      } ${
                        idx < 4 ? "md:max-lg:border-b border-[#EAF2FA]" : ""
                      } ${
                        idx < 5 ? "max-md:border-b border-[#EAF2FA]" : ""
                      }`}
                    >
                      {/* Icon Circle Badge */}
                      <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-[#EBF5FF] text-[#007AFF]">
                        <Icon className="h-6 w-6 stroke-[2]" />
                      </div>

                      {/* Content */}
                      <div className="flex flex-col gap-1.5 pt-0.5">
                        <h3 className="text-[16px] font-bold text-[#0F172A] leading-tight">
                          {item.title}
                        </h3>
                        <div className="text-[13px] sm:text-[13.5px] leading-[21px] text-[#64748B]">
                          {item.desc}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            The Context Section (matching Screenshot 1 exact layout & aspect ratio)
            Background: professional-context-bg.jpg (Consultant at night)
            Overlay: professional-context-overlay.png (Seamless left gradient)
            Left: The Context gradient heading + 2 narrative paragraphs (max-w-[420px])
            Center: Visible consultant typing on laptop
            Right: 2 Sleek Floating Glassmorphic UI Cards
           ========================================================================= */}
        <section
          id="the-context"
          className="relative w-full overflow-hidden bg-[#040A14] py-8 sm:py-10 lg:py-12 min-h-[440px] lg:min-h-[480px] flex items-center"
        >
          {/* Base Layer: Background Photo of Consultant at Night */}
          <div className="absolute inset-0 w-full h-full select-none pointer-events-none">
            <img
              src="/images/insights/professional-context-bg.jpg"
              alt="Professional Services Context"
              className="h-full w-full object-cover object-[center_bottom]"
            />
          </div>

          {/* Overlay Layer: Provided in Screenshot 3 (Dark Gradient Transition) */}
          <div className="absolute inset-0 w-full h-full select-none pointer-events-none">
            <img
              src="/images/insights/professional-context-overlay.png"
              alt=""
              className="h-full w-full object-cover object-[center_bottom] opacity-95"
            />
          </div>

          {/* Foreground Content */}
          <div className="relative z-10 mx-auto max-w-[1440px] w-full px-6 lg:px-[63px]">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-6 items-center">
              {/* Left Column: Heading & Narrative */}
              <div className="lg:col-span-5 xl:col-span-5 flex flex-col justify-center">
                {/* Heading row with gradient text and horizontal line */}
                <div className="flex items-center gap-5 sm:gap-6 mb-5 sm:mb-6">
                  <h2 className="text-[28px] sm:text-[34px] lg:text-[38px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                    The Context
                  </h2>
                  <div className="w-[120px] sm:w-[160px] lg:w-[200px] h-[1px] bg-white/20" />
                </div>

                {/* Narrative Paragraphs */}
                <div className="max-w-[420px] space-y-4 sm:space-y-5 text-[13px] sm:text-[13.5px] leading-[22px] sm:leading-[24px] text-[#CBD5E1]">
                  <p>
                    Professional services firms win a large share of work through RFPs and formal proposals. Each response is a chance to match the client&apos;s requirements with the firm&apos;s strongest proof — past projects, methodologies, expert profiles and clear answers to every section.
                  </p>
                  <p>
                    The challenge is scale: requirements must be understood quickly, the right material must surface from the firm&apos;s own library, and the team must shape a tailored draft in time for partner review and a confident submission.
                  </p>
                </div>
              </div>

              {/* Center Spacer to frame the consultant working on laptop */}
              <div className="hidden lg:block lg:col-span-2 xl:col-span-3" />

              {/* Right Column: Floating Glassmorphic Cards */}
              <div className="lg:col-span-5 xl:col-span-4 flex flex-col items-center lg:items-end gap-3.5">
                {/* Top Card: Relevant Content Found */}
                <div className="w-full max-w-[310px] sm:max-w-[330px] rounded-[14px] bg-[#0A1424]/85 backdrop-blur-md border border-white/[0.08] p-4 sm:p-4.5 shadow-[0_16px_40px_rgba(0,0,0,0.55)]">
                  {/* Card Header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-[#00D2EE] shadow-[0_0_8px_#00D2EE]" />
                      <span className="text-[13px] sm:text-[13.5px] font-semibold text-white tracking-tight">
                        Relevant Content Found
                      </span>
                    </div>
                    <span className="text-[10.5px] font-mono text-[#00D2EE] font-medium tracking-tight">
                      Matched Library
                    </span>
                  </div>

                  {/* 4 List Items */}
                  <div className="space-y-1.5">
                    {[
                      { id: 1, label: "Past Proposals", count: "12+" },
                      { id: 2, label: "Case Studies", count: "8+" },
                      { id: 3, label: "Expert Profiles", count: "15+" },
                      { id: 4, label: "Methodology Docs", count: "20+" },
                    ].map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center justify-between rounded-[6px] bg-[#101C2E]/90 border border-white/[0.05] px-3 py-1.5 transition-colors hover:bg-[#14233A]"
                      >
                        <div className="flex items-center gap-2">
                          <span className="flex h-4 w-4 items-center justify-center rounded-[3px] bg-[#00D2EE]/20 text-[10px] font-bold text-[#00D2EE]">
                            {item.id}
                          </span>
                          <span className="text-[12px] font-medium text-slate-200">
                            {item.label}
                          </span>
                        </div>
                        <span className="rounded-[3px] bg-[#1A283D] px-1.5 py-0.5 text-[10.5px] font-bold text-slate-300">
                          {item.count}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Bottom Card: RFP Analysis Progress */}
                <div className="w-full max-w-[310px] sm:max-w-[330px] rounded-[12px] bg-[#0A1424]/85 backdrop-blur-md border border-white/[0.08] p-3.5 sm:p-4 shadow-[0_16px_40px_rgba(0,0,0,0.55)]">
                  {/* Card Header */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="relative flex h-3.5 w-3.5 items-center justify-center">
                        <span className="absolute h-full w-full rounded-full border border-[#00D2EE]/40 border-t-[#00D2EE]" />
                        <span className="h-1.5 w-1.5 rounded-full bg-[#00D2EE]" />
                      </div>
                      <span className="text-[12.5px] sm:text-[13px] font-semibold text-white">
                        RFP Analysis Progress
                      </span>
                    </div>
                    <span className="text-[12px] font-bold text-[#00D2EE]">80%</span>
                  </div>

                  {/* Progress Bar */}
                  <div className="h-1.5 w-full rounded-full bg-[#142236] overflow-hidden my-2.5">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-[#00D2EE] to-[#0095FF] shadow-[0_0_10px_rgba(0,210,238,0.5)]"
                      style={{ width: "80%" }}
                    />
                  </div>

                  {/* Status Footer */}
                  <div className="flex items-center justify-between text-[10.5px] text-slate-400">
                    <span>Mapping requirements...</span>
                    <span>42 / 52 Sections Verified</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            The Challenge Section (matching Screenshot)
            Left: Full-bleed / split RFP document stack image (/images/insights/professional-challenge.jpg)
            Right: Content area (White background)
              - The challenge heading with gradient text & horizontal divider line
              - Intro narrative paragraph
              - Two Cards (01 Time-consuming and manual, 02 Inconsistent and fragmented content)
              - Quote callout box with blue left accent border
           ========================================================================= */}
        <section
          id="the-challenge"
          className="w-full relative flex flex-col lg:flex-row items-stretch overflow-hidden bg-white text-slate-900"
        >
          {/* Left Column: Image flush to left edge, fills ~50% width on lg screens */}
          <div className="w-full lg:w-1/2 min-h-[380px] sm:min-h-[460px] lg:min-h-[540px] xl:min-h-[580px] relative shrink-0 bg-slate-100 overflow-hidden">
            <img
              src="/images/insights/professional-challenge.jpg"
              alt="RFP documents stack with binder clips"
              className="w-full h-full object-cover object-center block select-none"
            />
          </div>

          {/* Right Column: Content area */}
          <div className="w-full lg:w-1/2 flex flex-col justify-center px-6 sm:px-12 lg:px-12 xl:px-16 py-12 sm:py-16 lg:py-20">
            <div className="max-w-[580px] w-full mx-auto lg:mx-0 flex flex-col text-left">
              {/* Heading row with horizontal divider line */}
              <div className="flex items-center gap-6 mb-5 sm:mb-6">
                <h2 className="text-[28px] sm:text-[34px] lg:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                  The challenge
                </h2>
                <div className="flex-1 h-[1px] bg-[#E2E8F0]" />
              </div>

              {/* Intro Narrative */}
              <p className="text-[13.5px] sm:text-[14px] leading-[22px] sm:leading-[24px] text-[#64748B] mb-7 sm:mb-8">
                RFPs are complex, time-bound and require highly tailored responses. Teams often spend significant time understanding requirements, searching for the right content and assembling drafts, leaving less time for strategy and win themes.
              </p>

              {/* 2 Cards Grid: 01 & 02 */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 mb-6 sm:mb-7">
                {/* Card 01 */}
                <div className="rounded-[14px] sm:rounded-[16px] bg-[#F5FAFE] border border-[#E2EDF8] p-5 sm:p-6 flex flex-col">
                  <span className="text-[24px] sm:text-[26px] font-bold text-[#007AFF] tracking-tight leading-none">
                    01
                  </span>
                  <h3 className="text-[15px] sm:text-[16px] font-bold text-[#0F172A] mt-3 mb-2 leading-snug">
                    Time-consuming and manual
                  </h3>
                  <p className="text-[12.5px] sm:text-[13px] leading-[20px] text-[#64748B]">
                    Requirements need to be analysed and mapped across multiple sections, which is often done manually.
                  </p>
                </div>

                {/* Card 02 */}
                <div className="rounded-[14px] sm:rounded-[16px] bg-[#F5FAFE] border border-[#E2EDF8] p-5 sm:p-6 flex flex-col">
                  <span className="text-[24px] sm:text-[26px] font-bold text-[#007AFF] tracking-tight leading-none">
                    02
                  </span>
                  <h3 className="text-[15px] sm:text-[16px] font-bold text-[#0F172A] mt-3 mb-2 leading-snug">
                    Inconsistent and fragmented content
                  </h3>
                  <p className="text-[12.5px] sm:text-[13px] leading-[20px] text-[#64748B]">
                    Relevant case studies, methodologies and expert profiles are spread across different sources, leading to inconsistent responses.
                  </p>
                </div>
              </div>

              {/* Bottom Quote Callout Box */}
              <div className="w-full rounded-r-[10px] bg-[#F5FAFE] border-l-4 border-[#007AFF] py-3.5 sm:py-4 px-5 sm:px-6">
                <p className="text-[13.5px] sm:text-[14.5px] font-semibold italic text-[#1E293B] leading-relaxed">
                  &ldquo;This makes it difficult to consistently showcase the firm&apos;s best work and respond with speed and confidence.&rdquo;
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            The Insights Section (matching Screenshot)
            Background Color: rgba(8, 19, 31, 1)
            Background Image: /images/insights/professional-insights.jpg
            Left: The Insights heading (gradient) + 2 Narrative paragraphs
            Right: Simpel RFP Analysis software interface on laptop
           ========================================================================= */}
        <section
          id="the-insights"
          className="relative w-full overflow-hidden py-16 sm:py-20 lg:py-24"
          style={{ backgroundColor: "rgba(8, 19, 31, 1)" }}
        >
          {/* Background Image on Right with Seamless Left Blend */}
          <div className="absolute top-0 right-0 h-full w-full lg:w-[62%] xl:w-[58%] select-none pointer-events-none overflow-hidden">
            <img
              src="/images/insights/professional-insights.jpg"
              alt="Simpel RFP Analysis Interface on Laptop"
              className="h-full w-full object-cover object-[right_center] block"
            />
            {/* Seamless Left Fade Gradient into rgba(8, 19, 31, 1) */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "linear-gradient(to right, rgba(8, 19, 31, 1) 0%, rgba(8, 19, 31, 0.95) 12%, rgba(8, 19, 31, 0.6) 30%, transparent 60%)",
              }}
            />
            {/* Subtle top and bottom edge blending */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "linear-gradient(to bottom, rgba(8, 19, 31, 0.35) 0%, transparent 15%, transparent 85%, rgba(8, 19, 31, 0.35) 100%)",
              }}
            />
          </div>

          {/* Foreground Content */}
          <div className="relative z-10 mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            <div className="max-w-[480px] sm:max-w-[500px] flex flex-col text-left">
              {/* Heading row with horizontal divider line */}
              <div className="flex items-center gap-6 mb-6 sm:mb-8">
                <h2 className="text-[30px] sm:text-[36px] lg:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                  The Insights
                </h2>
                <div className="w-[120px] sm:w-[160px] lg:w-[200px] h-[1px] bg-white/20" />
              </div>

              {/* Narrative Paragraphs */}
              <div className="space-y-5 sm:space-y-6 text-[13.5px] sm:text-[14px] lg:text-[14.5px] leading-[24px] sm:leading-[26px] text-[#CBD5E1]">
                <p>
                  Simpel AI analyses the RFP, maps requirements to the firm&apos;s response structure and retrieves the most relevant proposals, case studies, CVs and methodology text from the approved library.
                </p>
                <p>
                  This gives the team a strong starting point to refine and tailor to the client&apos;s language and priorities — ensuring consistency, higher quality and faster turnaround.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            SECTION: THE APPROACH (A structured process from RFP to tailored response.)
            Background: the-approach-bg.png (mountain summit with ascending trajectory)
            4 Stepped Columns: 01 Analyze, 02 Retrieve, 03 Draft, 04 Review & Submit
           ========================================================================= */}
        <section
          id="the-approach"
          className="relative w-full overflow-hidden bg-white pt-16 sm:pt-20 lg:pt-24 pb-12 sm:pb-16 text-slate-900"
        >
          {/* Background Mountain Image positioned at the bottom */}
          <div className="absolute inset-x-0 bottom-0 h-[360px] sm:h-[420px] lg:h-[480px] pointer-events-none select-none z-0">
            <img
              src="/images/insights/the-approach-bg.png"
              alt="Mountain summit and trajectory path"
              className="w-full h-full object-cover object-bottom"
            />
            {/* Top white gradient fade for seamless transition */}
            <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-white via-white/80 to-transparent" />
          </div>

          {/* Main Content Container */}
          <div className="relative z-10 mx-auto max-w-[1440px] w-full px-6 lg:px-[70px]">
            {/* 1. Header with brand gradient & horizontal line */}
            <div className="flex items-center justify-between mb-6 sm:mb-8">
              <h2 className="text-[32px] sm:text-[38px] lg:text-[42px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                The Approach
              </h2>
              <div className="hidden sm:block flex-1 h-[1px] bg-[#E2E8F0] ml-8" />
            </div>

            {/* 2. Subheading & intro */}
            <div className="max-w-[800px] mb-8 sm:mb-10">
              <h3 className="text-[24px] sm:text-[28px] lg:text-[32px] font-bold text-[#0F172A] tracking-tight">
                A structured process from RFP to tailored response.
              </h3>
              <p className="mt-3 text-[13.5px] sm:text-[14px] text-[#64748B] leading-[22px]">
                Simpel Proposal &amp; RFP intelligence follows a clear, repeatable process to help teams move from RFP receipt to a high-quality, tailored draft in less time.
              </p>
            </div>

            {/* 3. The 4 Stepped Columns */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-0 relative">
              {/* Step 01 */}
              <div className="flex flex-col pr-0 lg:pr-8 lg:pt-28 relative">
                <span className="text-[36px] sm:text-[40px] font-bold text-[#A855F7] leading-none">
                  01
                </span>
                <h4 className="mt-3 text-[17px] sm:text-[18px] font-bold text-[#0F172A] leading-snug">
                  Analyze
                </h4>
                <p className="mt-2.5 text-[13px] sm:text-[13.5px] leading-[21px] text-[#64748B]">
                  Upload RFP and analyze requirements, mapping them to response sections.
                </p>
                <div className="hidden lg:block absolute right-0 top-1 h-[260px] w-[1px] bg-[#E2E8F0]" />
              </div>

              {/* Step 02 */}
              <div className="flex flex-col pl-0 lg:pl-8 pr-0 lg:pr-8 lg:pt-20 relative">
                <span className="text-[36px] sm:text-[40px] font-bold text-[#3B82F6] leading-none">
                  02
                </span>
                <h4 className="mt-3 text-[17px] sm:text-[18px] font-bold text-[#0F172A] leading-snug">
                  Retrieve
                </h4>
                <p className="mt-2.5 text-[13px] sm:text-[13.5px] leading-[21px] text-[#64748B]">
                  Find relevant past proposals, case studies, expert profiles and methodology content from the library.
                </p>
                <div className="hidden lg:block absolute right-0 top-1 h-[260px] w-[1px] bg-[#E2E8F0]" />
              </div>

              {/* Step 03 */}
              <div className="flex flex-col pl-0 lg:pl-8 pr-0 lg:pr-8 lg:pt-10 relative">
                <span className="text-[36px] sm:text-[40px] font-bold text-[#00D2EE] leading-none">
                  03
                </span>
                <h4 className="mt-3 text-[17px] sm:text-[18px] font-bold text-[#0F172A] leading-snug">
                  Draft
                </h4>
                <p className="mt-2.5 text-[13px] sm:text-[13.5px] leading-[21px] text-[#64748B]">
                  Get a structed starting draft with recommended content to refine and tailor.
                </p>
                <div className="hidden lg:block absolute right-0 top-1 h-[260px] w-[1px] bg-[#E2E8F0]" />
              </div>

              {/* Step 04 */}
              <div className="flex flex-col pl-0 lg:pl-8 lg:pt-0">
                <span className="text-[36px] sm:text-[40px] font-bold text-[#F97316] leading-none">
                  04
                </span>
                <h4 className="mt-3 text-[17px] sm:text-[18px] font-bold text-[#0F172A] leading-snug">
                  Review &amp; Submit
                </h4>
                <p className="mt-2.5 text-[13px] sm:text-[13.5px] leading-[21px] text-[#64748B]">
                  Refine with partners, add win themes and submit high-quality proposal.
                </p>
              </div>
            </div>

            {/* 4. Ascending Trajectory Curve & Step Badges Container */}
            <div className="relative w-full mt-10 sm:mt-14 lg:mt-16 h-[200px] sm:h-[230px] lg:h-[260px]">
              <svg
                className="absolute inset-0 w-full h-full pointer-events-none overflow-visible"
                viewBox="0 0 1200 240"
                fill="none"
                preserveAspectRatio="none"
              >
                <defs>
                  <linearGradient
                    id="profApproachTrajectoryGradient"
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="0%"
                  >
                    <stop offset="0%" stopColor="#A855F7" />
                    <stop offset="30%" stopColor="#3B82F6" />
                    <stop offset="62%" stopColor="#00D2EE" />
                    <stop offset="100%" stopColor="#F97316" />
                  </linearGradient>
                </defs>

                <path
                  d="M 40,215 C 180,210 320,202 460,196 C 600,190 670,182 740,172 C 830,160 930,135 1020,112 C 1070,95 1115,70 1148,55"
                  stroke="url(#profApproachTrajectoryGradient)"
                  strokeWidth="6"
                  strokeOpacity="0.25"
                  strokeLinecap="round"
                />

                <path
                  d="M 40,215 C 180,210 320,202 460,196 C 600,190 670,182 740,172 C 830,160 930,135 1020,112 C 1070,95 1115,70 1148,55"
                  stroke="url(#profApproachTrajectoryGradient)"
                  strokeWidth="3.5"
                  strokeLinecap="round"
                />

                <path
                  d="M 1160,39 L 1138,48 L 1148,55 L 1154,62 Z"
                  fill="#F97316"
                />

                <line x1="180" y1="168" x2="180" y2="208" stroke="#A855F7" strokeWidth="1.5" strokeDasharray="2 3" />
                <line x1="460" y1="158" x2="460" y2="196" stroke="#3B82F6" strokeWidth="1.5" strokeDasharray="2 3" />
                <line x1="740" y1="130" x2="740" y2="172" stroke="#00D2EE" strokeWidth="1.5" strokeDasharray="2 3" />
                <line x1="1020" y1="68" x2="1020" y2="112" stroke="#F97316" strokeWidth="1.5" strokeDasharray="2 3" />

                <circle cx="180" cy="208" r="5" fill="#A855F7" />
                <circle cx="460" cy="196" r="5" fill="#3B82F6" />
                <circle cx="740" cy="172" r="5" fill="#00D2EE" />
                <circle cx="1020" cy="112" r="5" fill="#F97316" />
              </svg>

              {/* 4 Icon Badges */}
              <div className="absolute transform -translate-x-1/2 -translate-y-1/2" style={{ left: "15%", top: "72%" }}>
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full overflow-hidden shadow-[0_6px_20px_rgba(168,85,247,0.25)] hover:scale-105 transition-transform duration-300">
                  <img src="/images/insights/approach-icon-1.png" alt="01 - Analyze" className="w-full h-full object-contain" />
                </div>
              </div>

              <div className="absolute transform -translate-x-1/2 -translate-y-1/2" style={{ left: "38.3%", top: "67%" }}>
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full overflow-hidden shadow-[0_6px_20px_rgba(59,130,246,0.25)] hover:scale-105 transition-transform duration-300">
                  <img src="/images/insights/approach-icon-2.png" alt="02 - Retrieve" className="w-full h-full object-contain" />
                </div>
              </div>

              <div className="absolute transform -translate-x-1/2 -translate-y-1/2" style={{ left: "61.7%", top: "54%" }}>
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full overflow-hidden shadow-[0_6px_20px_rgba(0,210,238,0.25)] hover:scale-105 transition-transform duration-300">
                  <img src="/images/insights/approach-icon-3.png" alt="03 - Draft" className="w-full h-full object-contain" />
                </div>
              </div>

              <div className="absolute transform -translate-x-1/2 -translate-y-1/2" style={{ left: "85%", top: "27%" }}>
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full overflow-hidden shadow-[0_6px_20px_rgba(249,115,22,0.25)] hover:scale-105 transition-transform duration-300">
                  <img src="/images/insights/approach-icon-4.png" alt="04 - Review & Submit" className="w-full h-full object-contain" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            SECTION: THE IMPACT (Faster responses. Higher quality. More wins.)
            Background: rgba(245, 250, 254, 1)
            KPI Table with vibrant blue header & highlight
            Bottom Quote Card with Practice Head attribution
           ========================================================================= */}
        <section
          id="the-impact"
          className="w-full py-16 sm:py-20 lg:py-24 text-slate-900"
          style={{ backgroundColor: "rgba(245, 250, 254, 1)" }}
        >
          <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            {/* Header row with gradient text and horizontal divider */}
            <div className="flex items-center justify-between mb-6 sm:mb-8">
              <h2 className="text-[30px] sm:text-[36px] lg:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                The Impact
              </h2>
              <div className="hidden sm:block flex-1 h-[1px] bg-[#E2E8F0] ml-8" />
            </div>

            {/* Subtitle & Description */}
            <div className="max-w-[800px] mb-8 sm:mb-10">
              <h3 className="text-[24px] sm:text-[28px] lg:text-[32px] font-bold text-[#0F172A] tracking-tight">
                Faster responses. Higher quality. More wins.
              </h3>
              <p className="mt-2 text-[13.5px] sm:text-[14px] text-[#64748B]">
                Teams using Simpel Proposal &amp; RFP Intelligence see significant improvements in speed, efficiency and response quality.
              </p>
            </div>

            {/* KPI Results Table */}
            <div className="w-full rounded-[12px] bg-white border border-[#E2EDF8] shadow-[0_2px_12px_rgba(15,23,42,0.03)] overflow-hidden mb-8 sm:mb-10">
              {/* Table Header */}
              <div className="grid grid-cols-1 md:grid-cols-12 bg-[#1E5EFF] px-6 sm:px-8 py-3.5 text-white text-[12px] font-bold tracking-wider uppercase">
                <div className="md:col-span-8">KPI</div>
                <div className="md:col-span-4 text-left md:text-left">INDICATIVE RESULT</div>
              </div>

              {/* Table Rows */}
              <div className="divide-y divide-[#F1F5F9]">
                {[
                  {
                    kpi: "Time from RFP receipt to first tailored draft",
                    result: "45% to 60% faster",
                    highlight: false,
                  },
                  {
                    kpi: "Partner / senior hours focused on strategy vs content assembly",
                    result: "35% to 50% more capacity for strategy and pricing",
                    highlight: false,
                  },
                  {
                    kpi: "Reuse of prior proof points and case material in new bids",
                    result: "Clear increase in systematic reuse",
                    highlight: false,
                  },
                  {
                    kpi: "Completeness and relevance of first drafts",
                    result: "Material improvement in coverage of RFP sections",
                    highlight: false,
                  },
                  {
                    kpi: "Typical payback window",
                    result: "9 – 15 months",
                    highlight: false,
                  },
                  {
                    kpi: "First-year value (capacity + response quality)",
                    result: "$200k – $480k (scale-dependent)",
                    highlight: true,
                  },
                ].map((row, idx) => (
                  <div
                    key={idx}
                    className="grid grid-cols-1 md:grid-cols-12 px-6 sm:px-8 py-4 sm:py-4.5 items-center transition-colors hover:bg-[#F8FAFC]/80 text-[13.5px] sm:text-[14px]"
                  >
                    <div className="md:col-span-8 text-[#0F172A] font-medium pr-4">
                      {row.kpi}
                    </div>
                    <div
                      className={`md:col-span-4 font-bold ${
                        row.highlight ? "text-[#007AFF] text-[15px]" : "text-[#0F172A]"
                      }`}
                    >
                      {row.result}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Bottom Quote Card */}
            <div className="w-full rounded-[14px] bg-[#EBF4FE] border border-[#D5E6FA] p-6 sm:p-8 flex items-start gap-5 sm:gap-6">
              {/* Cyan quotation mark icon */}
              <div className="shrink-0 text-[#00D2EE]">
                <svg
                  className="w-10 h-10 sm:w-12 sm:h-12"
                  viewBox="0 0 48 48"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M14 16C10.7 16 8 18.7 8 22C8 25.3 10.7 28 14 28C14 32.4 10.4 36 6 36V40C13.7 40 20 33.7 20 26V16H14Z"
                    fill="currentColor"
                  />
                  <path
                    d="M34 16C30.7 16 28 18.7 28 22C28 25.3 30.7 28 34 28C34 32.4 30.4 36 26 36V40C33.7 40 40 33.7 40 26V16H34Z"
                    fill="currentColor"
                  />
                </svg>
              </div>

              {/* Quote Content */}
              <div className="flex flex-col justify-center">
                <p className="text-[14px] sm:text-[15px] italic text-[#334155] leading-relaxed">
                  &ldquo;Once the system analyzed the RFP and put the right past work in front of us, the first draft was already close. We spent the remaining time making it win — shaping strategy, proof and tone for that client.&rdquo;
                </p>
                <div className="mt-4 flex items-center gap-3">
                  <div className="w-6 h-[2px] bg-[#007AFF]" />
                  <span className="text-[13px] sm:text-[13.5px] font-medium text-[#007AFF]">
                    Practice Head , Professional Services Firm
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            SECTION: WHAT OTHER LENDERS CAN TAKE FROM THIS (Figma Exact Specs)
            Figma Specs:
              - Width: 1,440px (max-w-[1440px])
              - Height: 538px (min-h-[538px])
              - Padding: Top 56px, Bottom 56px, Left 63px, Right 63px
              - Gradient: linear-gradient(180deg, rgba(0, 47, 67, 0.65) 0%, rgba(0, 34, 119, 0.70) 100%)
              - Background image: professional-lenders-bg.png
           ========================================================================= */}
        <section
          id="what-other-lenders-can-take"
          className="relative w-full overflow-hidden bg-[#001D33] min-h-[538px] flex items-center text-white"
        >
          {/* Base Layer: Background Image */}
          <div className="absolute inset-0 w-full h-full select-none pointer-events-none">
            <img
              src="/images/insights/professional-lenders-bg.png"
              alt="Financial and market intelligence dashboard meeting"
              className="w-full h-full object-cover object-center"
            />
            {/* Linear Gradient Overlay from Figma Inspector: rgba(0, 47, 67) to rgba(0, 34, 119) */}
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(180deg, rgba(0, 47, 67, 0.66) 0%, rgba(0, 34, 119, 0.70) 100%)",
              }}
            />
          </div>

          {/* Foreground Content */}
          <div className="relative z-10 mx-auto max-w-[1440px] w-full px-6 lg:px-[63px] py-10 lg:py-[56px] flex flex-col justify-between h-full">
            <div>
              {/* Header row with gradient heading + divider */}
              <div className="flex items-center gap-6 mb-2">
                <h2 className="text-[28px] sm:text-[34px] lg:text-[38px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                  What other lenders can take from this
                </h2>
                <div className="hidden sm:block flex-1 h-[1px] bg-white/20" />
              </div>

              {/* Subheading */}
              <p className="text-[19px] sm:text-[22px] lg:text-[24px] font-bold text-white mb-7 sm:mb-8">
                Act now. Turn data into faster, safer lending.
              </p>

              {/* 3 Glass Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6">
                {/* Card 01 */}
                <div className="rounded-[16px] bg-[#071324]/60 backdrop-blur-md border border-white/[0.12] p-5 sm:p-6 flex flex-col justify-between transition-all duration-300 hover:border-white/25 hover:bg-[#09182E]/70 shadow-[0_12px_40px_rgba(0,0,0,0.35)]">
                  <div>
                    <div className="flex items-center justify-between mb-3.5">
                      <span className="text-[30px] sm:text-[32px] font-bold text-[#00D2EE] leading-none">
                        01
                      </span>
                      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#11243E] text-[#00D2EE]">
                        <Copy className="h-4.5 w-4.5 stroke-[2]" />
                      </div>
                    </div>
                    <h3 className="text-[16px] sm:text-[17px] font-bold text-white mb-2 leading-snug">
                      Leverage your best work
                    </h3>
                    <p className="text-[12.5px] sm:text-[13px] leading-[20px] text-slate-300">
                      Make proven expertise accessible across the firm so no team reinvents previous success.
                    </p>
                  </div>
                  <div className="mt-6 pt-1">
                    <span className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#00D2EE] hover:underline cursor-pointer group">
                      <span>Explore Capability</span>
                      <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </div>

                {/* Card 02 */}
                <div className="rounded-[16px] bg-[#071324]/60 backdrop-blur-md border border-white/[0.12] p-5 sm:p-6 flex flex-col justify-between transition-all duration-300 hover:border-white/25 hover:bg-[#09182E]/70 shadow-[0_12px_40px_rgba(0,0,0,0.35)]">
                  <div>
                    <div className="flex items-center justify-between mb-3.5">
                      <span className="text-[30px] sm:text-[32px] font-bold text-[#00D2EE] leading-none">
                        02
                      </span>
                      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#11243E] text-[#00D2EE]">
                        <Zap className="h-4.5 w-4.5 stroke-[2]" />
                      </div>
                    </div>
                    <h3 className="text-[16px] sm:text-[17px] font-bold text-white mb-2 leading-snug">
                      Respond faster with confidence
                    </h3>
                    <p className="text-[12.5px] sm:text-[13px] leading-[20px] text-slate-300">
                      Reduce manual effort and improve consistency across complex multi-stakeholder submissions.
                    </p>
                  </div>
                  <div className="mt-6 pt-1">
                    <span className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#00D2EE] hover:underline cursor-pointer group">
                      <span>View Benchmark</span>
                      <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </div>

                {/* Card 03 */}
                <div className="rounded-[16px] bg-[#071324]/60 backdrop-blur-md border border-white/[0.12] p-5 sm:p-6 flex flex-col justify-between transition-all duration-300 hover:border-white/25 hover:bg-[#09182E]/70 shadow-[0_12px_40px_rgba(0,0,0,0.35)]">
                  <div>
                    <div className="flex items-center justify-between mb-3.5">
                      <span className="text-[30px] sm:text-[32px] font-bold text-[#00D2EE] leading-none">
                        03
                      </span>
                      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#11243E] text-[#00D2EE]">
                        <Sparkles className="h-4.5 w-4.5 stroke-[2]" />
                      </div>
                    </div>
                    <h3 className="text-[16px] sm:text-[17px] font-bold text-white mb-2 leading-snug">
                      Focus on strategy and win themes
                    </h3>
                    <p className="text-[12.5px] sm:text-[13px] leading-[20px] text-slate-300">
                      Give partners more time for high-value decisions, commercial packaging and executive relationships.
                    </p>
                  </div>
                  <div className="mt-6 pt-1">
                    <span className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#00D2EE] hover:underline cursor-pointer group">
                      <span>Learn More</span>
                      <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Callout Banner */}
            <div className="mt-8 border-l-2 border-white pl-4 py-0.5 text-[12.5px] sm:text-[13px] text-slate-200 leading-relaxed">
              <span className="font-bold text-white">The opportunity is now.</span>
              <span> As borrower data becomes easier to access, the advantagegoes to lenders who can turn it into faster, explainable decisions.</span>
            </div>
          </div>
        </section>
      </main>

      {/* Global Footer */}
      <Footer />
    </div>
  );
};

export default ProfessionalServicesCaseStudy;

